"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTranslations } from "next-intl";
import LocaleSwitcher from "@/components/i18n/LocaleSwitcher";
import { useState, useEffect } from "react";

export default function PrivacyPolicyClient() {
  const t = useTranslations("Privacy");
  const lastUpdated = new Date("2025-05-05").toLocaleDateString();
  const [mailtoLink, setMailtoLink] = useState("contact@taberu.ai");

  // Set mailto link only after component mounts to prevent hydration mismatch
  useEffect(() => {
    setMailtoLink("mailto:contact@taberu.ai");
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6 flex justify-between items-center">
        <Link href={"/" as const}>
          <Button variant="ghost" size="sm" className="gap-2">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {t("back")}
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          <ThemeToggle />
        </div>
      </div>
      <main>
        <Card>
          <CardContent className="p-6 prose dark:prose-invert max-w-none">
            <h1 className="text-3xl font-bold mb-6">{t("title")}</h1>
            <p className="text-sm text-muted-foreground mb-4">
              <time dateTime="2025-05-05">
                {t("lastUpdated")}: {lastUpdated}
              </time>
            </p>

            <section className="mb-8" aria-labelledby="introduction">
              <h2 id="introduction" className="text-2xl font-semibold mb-4">
                {t("sections.introduction.title")}
              </h2>
              <p>{t("sections.introduction.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="information-collected">
              <h2
                id="information-collected"
                className="text-2xl font-semibold mb-4"
              >
                {t("sections.informationCollected.title")}
              </h2>

              <section aria-labelledby="information-provided">
                <h3
                  id="information-provided"
                  className="text-xl font-semibold mb-3"
                >
                  {t("sections.informationCollected.provided.title")}
                </h3>
                <ul className="list-disc pl-6 mb-4">
                  <li>
                    {t("sections.informationCollected.provided.items.account")}
                  </li>
                  <li>
                    {t("sections.informationCollected.provided.items.profile")}
                  </li>
                  <li>
                    {t("sections.informationCollected.provided.items.content")}
                  </li>
                  <li>
                    {t("sections.informationCollected.provided.items.uploaded")}
                  </li>
                  <li>
                    {t("sections.informationCollected.provided.items.payment")}
                  </li>
                  <li>
                    {t("sections.informationCollected.provided.items.business")}
                  </li>
                </ul>
              </section>

              <section aria-labelledby="information-automatic">
                <h3
                  id="information-automatic"
                  className="text-xl font-semibold mb-3"
                >
                  {t("sections.informationCollected.automatic.title")}
                </h3>
                <ul className="list-disc pl-6 mb-4">
                  <li>
                    {t("sections.informationCollected.automatic.items.device")}
                  </li>
                  <li>
                    {t("sections.informationCollected.automatic.items.usage")}
                  </li>
                  <li>
                    {t("sections.informationCollected.automatic.items.ip")}
                  </li>
                  <li>
                    {t("sections.informationCollected.automatic.items.cookie")}
                  </li>
                  <li>
                    {t("sections.informationCollected.automatic.items.metrics")}
                  </li>
                  <li>
                    {t(
                      "sections.informationCollected.automatic.items.patterns"
                    )}
                  </li>
                </ul>
              </section>
            </section>

            <section className="mb-8" aria-labelledby="data-use">
              <h2 id="data-use" className="text-2xl font-semibold mb-4">
                {t("sections.dataUse.title")}
              </h2>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.dataUse.items.provide")}</li>
                <li>{t("sections.dataUse.items.process")}</li>
                <li>{t("sections.dataUse.items.track")}</li>
                <li>{t("sections.dataUse.items.payments")}</li>
                <li>{t("sections.dataUse.items.generate")}</li>
                <li>{t("sections.dataUse.items.enable")}</li>
                <li>{t("sections.dataUse.items.analytics")}</li>
                <li>{t("sections.dataUse.items.improve")}</li>
                <li>{t("sections.dataUse.items.communicate")}</li>
                <li>{t("sections.dataUse.items.protect")}</li>
                <li>{t("sections.dataUse.items.comply")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="ai-processing">
              <h2 id="ai-processing" className="text-2xl font-semibold mb-4">
                {t("sections.aiProcessing.title")}
              </h2>
              <p className="mb-4">{t("sections.aiProcessing.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.aiProcessing.items.ocr")}</li>
                <li>{t("sections.aiProcessing.items.gemini")}</li>
                <li>{t("sections.aiProcessing.items.secure")}</li>
                <li>{t("sections.aiProcessing.items.improve")}</li>
                <li>{t("sections.aiProcessing.items.ownership")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="credit-system">
              <h2 id="credit-system" className="text-2xl font-semibold mb-4">
                {t("sections.creditSystem.title")}
              </h2>
              <p className="mb-4">{t("sections.creditSystem.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>
                  <strong>{t("sections.creditSystem.tiers.free.title")}</strong>{" "}
                  {t("sections.creditSystem.tiers.free.content")}{" "}
                  <span className="text-muted-foreground text-sm">
                    {t("sections.creditSystem.tiers.free.note")}
                  </span>
                </li>
                <li>
                  <strong>
                    {t("sections.creditSystem.tiers.premium.title")}
                  </strong>{" "}
                  {t("sections.creditSystem.tiers.premium.content")}
                </li>
                <li>
                  <strong>
                    {t("sections.creditSystem.tiers.lifetime.title")}
                  </strong>{" "}
                  {t("sections.creditSystem.tiers.lifetime.content")}
                </li>
                <li>{t("sections.creditSystem.items.additional")}</li>
                <li>{t("sections.creditSystem.items.tracking")}</li>
                <li>{t("sections.creditSystem.items.reset")}</li>
                <li>{t("sections.creditSystem.items.available")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="cookies">
              <h2 id="cookies" className="text-2xl font-semibold mb-4">
                {t("sections.cookies.title")}
              </h2>
              <p className="mb-4">{t("sections.cookies.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.cookies.types.necessary")}</li>
                <li>{t("sections.cookies.types.analytics")}</li>
                <li>{t("sections.cookies.types.marketing")}</li>
                <li>{t("sections.cookies.types.preference")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="data-sharing">
              <h2 id="data-sharing" className="text-2xl font-semibold mb-4">
                {t("sections.dataSharing.title")}
              </h2>
              <p className="mb-4">{t("sections.dataSharing.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.dataSharing.items.providers")}</li>
                <li>{t("sections.dataSharing.items.apis")}</li>
                <li>{t("sections.dataSharing.items.legal")}</li>
                <li>{t("sections.dataSharing.items.partners")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="security">
              <h2 id="security" className="text-2xl font-semibold mb-4">
                {t("sections.security.title")}
              </h2>
              <p className="mb-4">{t("sections.security.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.security.measures.encryption")}</li>
                <li>{t("sections.security.measures.assessments")}</li>
                <li>{t("sections.security.measures.access")}</li>
                <li>{t("sections.security.measures.payments")}</li>
                <li>{t("sections.security.measures.database")}</li>
                <li>{t("sections.security.measures.apis")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="user-rights">
              <h2 id="user-rights" className="text-2xl font-semibold mb-4">
                {t("sections.userRights.title")}
              </h2>
              <p className="mb-4">{t("sections.userRights.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.userRights.rights.access")}</li>
                <li>{t("sections.userRights.rights.correct")}</li>
                <li>{t("sections.userRights.rights.delete")}</li>
                <li>{t("sections.userRights.rights.object")}</li>
                <li>{t("sections.userRights.rights.export")}</li>
                <li>{t("sections.userRights.rights.withdraw")}</li>
                <li>{t("sections.userRights.rights.manage")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="retention">
              <h2 id="retention" className="text-2xl font-semibold mb-4">
                {t("sections.retention.title")}
              </h2>
              <p>{t("sections.retention.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="international">
              <h2 id="international" className="text-2xl font-semibold mb-4">
                {t("sections.international.title")}
              </h2>
              <p>{t("sections.international.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="children">
              <h2 id="children" className="text-2xl font-semibold mb-4">
                {t("sections.children.title")}
              </h2>
              <p>{t("sections.children.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="qr-codes">
              <h2 id="qr-codes" className="text-2xl font-semibold mb-4">
                {t("sections.qrCodes.title")}
              </h2>
              <p className="mb-4">{t("sections.qrCodes.content")}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t("sections.qrCodes.items.link")}</li>
                <li>{t("sections.qrCodes.items.anonymous")}</li>
                <li>{t("sections.qrCodes.items.language")}</li>
                <li>{t("sections.qrCodes.items.personal")}</li>
                <li>{t("sections.qrCodes.items.statistics")}</li>
              </ul>
            </section>

            <section className="mb-8" aria-labelledby="changes">
              <h2 id="changes" className="text-2xl font-semibold mb-4">
                {t("sections.changes.title")}
              </h2>
              <p>{t("sections.changes.content")}</p>
            </section>

            <section className="mb-8" aria-labelledby="contact">
              <h2 id="contact" className="text-2xl font-semibold mb-4">
                {t("sections.contact.title")}
              </h2>
              <p>
                {t("sections.contact.content")}{" "}
                <a href={mailtoLink} className="text-primary hover:underline">
                  contact@taberu.ai
                </a>
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
